import type { ClarifyTurn, DayPlan, DebugEntry, Item, ItemPatch, PlanAction, Project, ProjectDraft, ProjectPatch, State } from '../types'

declare global {
  interface Window {
    KLARYGO_API?: string
  }
}

const API_BASE = window.KLARYGO_API ?? ''
const TOKEN_KEY = 'klarygo_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

/** Un 401 invalide la session : l'app entière doit repasser par le login. */
type Listener = () => void
const unauthorized = new Set<Listener>()
export function onUnauthorized(fn: Listener): () => void {
  unauthorized.add(fn)
  return () => unauthorized.delete(fn)
}

/**
 * Trace du coach : seuls /api/proposal et /api/clarify-turn en renvoient une.
 * La console de debug s'y abonne au lieu que le client connaisse son existence.
 */
export interface CoachCall {
  phase: 'start' | 'end'
  entries?: DebugEntry[]
  ms?: number
}
const coachCalls = new Set<(c: CoachCall) => void>()
export function onCoachCall(fn: (c: CoachCall) => void): () => void {
  coachCalls.add(fn)
  return () => coachCalls.delete(fn)
}
const COACH_PATHS = ['/api/proposal', '/api/clarify-turn', '/api/day-plan']

export class Unauthorized extends Error {}

async function api<T>(path: string, body?: unknown): Promise<T> {
  const token = getToken()
  const isCoach = COACH_PATHS.some((p) => path.startsWith(p))
  const t0 = performance.now()
  if (isCoach) coachCalls.forEach((fn) => fn({ phase: 'start' }))

  const res = await fetch(API_BASE + path, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (res.status === 401) {
    clearToken()
    unauthorized.forEach((fn) => fn())
    throw new Unauthorized('unauthorized')
  }
  const json = (await res.json()) as T & { debug?: DebugEntry[] }
  if (isCoach) {
    coachCalls.forEach((fn) => fn({ phase: 'end', entries: json.debug, ms: Math.round(performance.now() - t0) }))
  }
  return json
}

export async function login(email: string, password: string): Promise<string> {
  const res = await fetch(API_BASE + '/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) throw new Error('Identifiants incorrects.')
  const d = (await res.json()) as { token?: string }
  if (!d.token) throw new Error('Réponse sans token.')
  return d.token
}

export const getState = () => api<State>('/api/state')

export const capture = (title: string) => api<{ ok: boolean; item: Item }>('/api/capture', { title })

/** `item_id` absent => le back prend la plus ancienne capture de l'inbox. */
export const clarifyTurn = (body: { item_id?: string; history: { q: string; a: string }[]; hint?: string | null }) =>
  api<ClarifyTurn>('/api/clarify-turn', body)

export const clarifyCommit = (body: {
  item_id: string
  project: ProjectDraft | null
  actions: PlanAction[]
  domain: string | null
}) => api<{ ok: boolean }>('/api/clarify-commit', body)

export const updateItem = (id: string, patch: ItemPatch) => api<{ ok: boolean; item: Item }>('/api/update', { id, patch })

export const deleteItem = (id: string) => api<{ ok: boolean }>('/api/delete', { id })

/** Renommer / reformuler l'outcome / clôturer. Les items du projet ne bougent pas. */
export const updateProject = (id: string, patch: ProjectPatch) =>
  api<{ ok: boolean; project: Project }>('/api/project-update', { id, patch })

/** Le back répond `recurring: true` quand la tâche n'est pas close mais faite pour ce tour. */
export const markDone = (id: string) => api<{ ok: boolean; recurring?: boolean; item?: Item }>('/api/done', { id })

/** Crée un projet ; `item_id` le rattache dans le même appel. */
export const createProject = (body: { title: string; outcome?: string; item_id?: string }) =>
  api<{ ok: boolean; project: Project; item: Item | null }>('/api/project', body)

/** La sélection d'un jour, les faites comprises. */
export const getDay = (date?: string) => api<{ date: string; items: Item[] }>('/api/day' + (date ? '?date=' + date : ''))

/** Planifier ou déplanifier : 'today' | 'tomorrow' | date ISO | null. */
export const planItem = (id: string, date: string | null) => api<{ ok: boolean; item: Item }>('/api/plan', { id, date })

/** Le coach compose la journée. Propose seulement — rien n'est écrit. */
export const askDayPlan = (date?: string) => api<DayPlan>('/api/day-plan' + (date ? '?date=' + date : ''))

/** Enregistre ce que l'utilisateur a gardé. */
export const commitDayPlan = (date: string, ids: string[]) =>
  api<{ ok: boolean; date: string; planned: number }>('/api/day-plan', { date, ids })

export const addComment = (id: string, text: string) =>
  api<{ ok: boolean; comments: Item['comments'] }>('/api/comment', { id, text })
